import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';
import { 
  Copy, Check, ExternalLink, ArrowLeft, Package, Truck, 
  Clock, ShieldCheck, ShoppingBag, 
  Upload, X, Hash, HelpCircle,
  AlertTriangle, RotateCcw, Ban, Box, RefreshCw, Store, Phone
} from 'lucide-react';

// --- IMPORTS GENERALES ---
import { NavbarPublic } from '../../../components/NavbarPublic.jsx';
import { api } from '@/lib/api.ts';
import { formatCurrency, formatDate } from '../../../lib/format.ts';
import { usePedidoActions } from '../../../features/pedidos/usePedidos.ts';
import { playNotificationSound } from '../../../lib/notificationSound.ts';
import { usePublicStorePricingRate, formatDualPrice } from '../../../lib/dolar.ts';

// --- IMPORTS LOCALES REFACTORIZADOS ---
import { getAgencyConfig } from './helpers';
import AmbientBackground from './components/AmbientBackground';
import RefundModal from './modals/RefundModal';
import CancelModal from './modals/CancelModal';
import RegisterReturnShippingModal from './modals/RegisterReturnShippingModal';

export function OrdenResumen() {
  const { ordenId } = useParams();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Estados
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedTracking, setCopiedTracking] = useState(false);
  const previousStatusRef = useRef(null);
  const [file, setFile] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  
  // Modales
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showReturnShippingModal, setShowReturnShippingModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false); 
  
  // Queries
  const { data: pedido, isLoading, error } = useQuery({
    queryKey: ['orden', ordenId, email],
    enabled: Boolean(ordenId) && Boolean(email),
    refetchInterval: 10000,   
    staleTime: 30000,         
    queryFn: async () => {
      const { data } = await api.get(`/api/orders/${ordenId}`, { params: { email } });
      return data;
    },
    retry: 1,
  });

  const { data: storeInfo } = useQuery({
    queryKey: ['tienda', pedido?.store_slug],
    enabled: Boolean(pedido?.store_slug),
    staleTime: 600000,        
    refetchInterval: false,   
    queryFn: async () => (await api.get(`/api/stores/${pedido?.store_slug}/public`)).data
  });
  
  const { data: pricingRate } = usePublicStorePricingRate(pedido?.store_slug);
  const { attachProof } = usePedidoActions();

  // --- MUTACIONES ---
  const requestRefundMutation = useMutation({
    mutationFn: async (data) => await api.post(`/api/orders/${ordenId}/request-refund`, data),
    onSuccess: () => {
        toast.success('Solicitud de devolución enviada');
        queryClient.invalidateQueries({ queryKey: ['orden', ordenId, email] });
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Error al solicitar devolución')
  });

  useEffect(() => {
    if (!pedido || !pedido.status) return;
    if (previousStatusRef.current === null) {
      previousStatusRef.current = pedido.status;
      return;
    }
    if (previousStatusRef.current !== pedido.status) {
      playNotificationSound();
      toast.success('El estado de tu pedido ha cambiado');
      previousStatusRef.current = pedido.status;
    }
  }, [pedido?.status]);

  const handleProofUpload = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile || !ordenId) return;
    setFile(selectedFile); 
    
    attachProof.mutate(
        { pedidoId: ordenId, proof: selectedFile, email },
        {
            onSuccess: () => {
                setFile(null); 
                queryClient.invalidateQueries({ queryKey: ['orden', ordenId, email] });
                toast.success('Comprobante enviado. Verificando...');
            },
            onError: (err) => {
                setFile(null);
                toast.error(err.response?.data?.error || 'Error al subir el comprobante');
            }
        }
    );
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      toast.success('Link copiado');
      setTimeout(() => setCopiedLink(false), 2000);
    } catch { toast.error('Error al copiar'); }
  };

  const handleCopyTracking = async () => {
    if (!pedido?.shipping_tracking_number) return;
    try {
        await navigator.clipboard.writeText(pedido.shipping_tracking_number);
        setCopiedTracking(true);
        toast.success('Tracking copiado');
        setTimeout(() => setCopiedTracking(false), 2000);
    } catch { toast.error('Error al copiar tracking'); }
  };

  const handleSmartTracking = (url, trackingNumber) => {
    navigator.clipboard.writeText(trackingNumber)
      .then(() => toast.success('Código copiado'))
      .catch(() => toast.error('No se pudo copiar automáticamente'));
    window.open(url, '_blank');
  };

  if (isLoading) return <div className="min-h-screen bg-black flex items-center justify-center text-white/30 text-xs uppercase tracking-widest animate-pulse">Cargando datos...</div>;
  if (error || !pedido) return <div className="min-h-screen bg-black flex items-center justify-center text-red-400/50 text-xs uppercase tracking-widest">Error: Datos no encontrados</div>;

  const agencyConfig = getAgencyConfig(pedido.shipping_agency);

  const statusConfig = {
    pending_payment_confirmation: { 
      color: pedido.receipt_correction_requested ? 'text-red-400' : 'text-amber-400', 
      bg: pedido.receipt_correction_requested ? 'bg-red-500/10' : 'bg-amber-500/10', 
      border: pedido.receipt_correction_requested ? 'border-red-500/30' : 'border-amber-500/30', 
      icon: pedido.receipt_correction_requested ? AlertTriangle : Clock, 
      label: pedido.receipt_correction_requested ? 'Comprobante Rechazado' : 'Verificando Pago', 
      description: pedido.receipt_correction_requested ? 'El comprobante que subiste no es válido. Por favor sube uno correcto.' : 'Estamos validando tu comprobante.' 
    },
    payment_confirmed: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', icon: Check, label: 'Pago Confirmado', description: 'Hemos recibido tu pago correctamente.' },
    ready_for_pickup: { color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', icon: Package, label: 'Listo para Retiro', description: '¡Tu pedido te espera en la tienda!' },
    out_for_delivery: { color: 'text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', icon: Truck, label: 'En Camino', description: 'Nuestro motorizado va en camino.' },
    pending_agency_delivery: { color: 'text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', icon: Truck, label: 'Enviado por Agencia', description: `Entregado a la agencia ${pedido.shipping_agency || ''}.` }, 
    delivered_confirmed: { color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: ShieldCheck, label: 'Entregado', description: 'El pedido ha sido entregado con éxito.' },
    cancelled: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: X, label: 'Cancelado', description: 'Este pedido ha sido cancelado.' },
    
    cancellation_requested: { 
        color: 'text-orange-400', 
        bg: 'bg-orange-500/10', 
        border: 'border-orange-500/30', 
        icon: AlertTriangle, 
        label: 'Cancelación Solicitada', 
        description: 'Hemos recibido tu solicitud. Estamos procesando tu reembolso.' 
    },
    return_requested: { color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', icon: RotateCcw, label: 'Revisando Solicitud', description: 'La tienda está analizando tu solicitud de devolución.' },
    return_approved_waiting_shipping: { color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', icon: Box, label: 'Solicitud Aprobada', description: 'Por favor envía el producto de vuelta siguiendo las instrucciones.' },
    return_shipped_by_customer: { color: 'text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', icon: Truck, label: 'Devolución en Camino', description: 'Has enviado el producto. Esperando recepción en tienda.' },
    return_received_in_store: { color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30', icon: Clock, label: 'Producto Recibido', description: 'La tienda está verificando el estado del producto.' },
    return_completed_refunded: { color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30', icon: Check, label: 'Dinero Reembolsado', description: 'El proceso ha finalizado. Tu dinero ha sido devuelto.' },
    return_completed_replaced: { color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', icon: RefreshCw, label: 'Producto Cambiado', description: 'Se ha enviado tu nuevo producto de reemplazo.' },
    return_rejected: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: Ban, label: 'Solicitud Rechazada', description: 'La tienda no ha aprobado la devolución.' },
    
    default: { color: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/30', icon: Clock, label: 'Procesando', description: `Tu orden #${pedido.id} está siendo procesada.` }
  };

  const currentStatus = statusConfig[pedido.status] || statusConfig.default;
  const StatusIcon = currentStatus.icon;

  const showCancelButton = pedido?.can_cancel;
  const cancelNeedsRefund = pedido.status === 'payment_confirmed' || (pedido.status === 'pending_payment_confirmation' && (pedido.payment_receipt || pedido.payment_receipt_url));
  const showRefundButton = (pedido.status === 'delivered_confirmed' && !pedido.refund_request);
  const showReturnInstructions = pedido.status === 'return_approved_waiting_shipping';
  const isNationalShipping = pedido.shipping_method === 'national_shipping';

  const existingPaymentData = {
      banco: pedido.payment_bank_origin || '',
      identificacion: pedido.payment_payer_id || '',
      telefono: pedido.payment_payer_phone || ''
  };

  return (
    <div className="min-h-screen text-slate-200 font-sans selection:bg-emerald-500/30 flex flex-col">
      <AmbientBackground />
      <NavbarPublic />
      
      <RefundModal 
        isOpen={showRefundModal} 
        onClose={() => setShowRefundModal(false)}
        orderId={ordenId}
        email={email}
        onSubmit={requestRefundMutation.mutateAsync}
        initialPaymentData={existingPaymentData} 
      />

      <CancelModal 
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        orderId={ordenId}
        email={email}
        needsRefund={cancelNeedsRefund}
        initialPaymentData={existingPaymentData}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['orden', ordenId, email] })}
      />

      <RegisterReturnShippingModal
        isOpen={showReturnShippingModal}
        onClose={() => setShowReturnShippingModal(false)}
        orderId={ordenId}
        email={email}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['orden', ordenId, email] })}
      />
      
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-12 relative z-10">
        
        <button onClick={() => navigate(-1)} className="group inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors mb-6">
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" /> Volver
        </button>

        {storeInfo && (
          <div className="rounded-[2rem] border border-white/10 bg-[#0F0F0F]/60 backdrop-blur-md p-6 mb-8">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                {storeInfo.logo ? (
                  <img 
                    src={storeInfo.logo.startsWith('http') ? storeInfo.logo : `https://imagenes.tiendasya.store/${storeInfo.logo.startsWith('/') ? storeInfo.logo.slice(1) : storeInfo.logo}`}
                    alt={`Logo de ${storeInfo.name}`}
                    className="w-16 h-16 rounded-full object-cover bg-white border-2 border-white/10"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-white/10 border-2 border-white/10 flex items-center justify-center">
                    <Store className="h-8 w-8 text-white/40" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1">Pedido de</p>
                <h2 className="text-xl font-bold text-white mb-1">{storeInfo.name}</h2>
                {storeInfo.description && (
                  <p className="text-sm text-white/60 line-clamp-2">{storeInfo.description}</p>
                )}
              </div>
              <div className="flex-shrink-0">
                <a href={`/t/${storeInfo.slug}`} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 border border-white/10 text-white/80 hover:bg-white/20 hover:text-white transition-all text-xs font-bold uppercase tracking-widest">
                  <Store className="h-3 w-3" /> Ver Tienda
                </a>
              </div>
            </div>
          </div>
        )}

        {pedido.status === 'payment_confirmed' && pedido.refund_request?.status === 'rejected' && (
          <div className="rounded-[2rem] border border-red-500/30 bg-red-500/10 backdrop-blur-md p-6 mb-6 relative overflow-hidden animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="absolute top-0 right-0 p-6 opacity-5">
              <Ban className="w-24 h-24 text-red-400" />
            </div>
            <div className="relative z-10 flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                <Ban className="h-6 w-6 text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-red-400 mb-1">Solicitud de Cancelación Rechazada</h3>
                <p className="text-white/80 text-sm leading-relaxed mb-3">La tienda ha rechazado tu solicitud de cancelación.</p>
                {pedido.refund_request?.merchant_notes && (
                  <div className="bg-black/30 border border-red-500/20 rounded-xl p-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-red-400/70 mb-1">Motivo del Rechazo</p>
                    <p className="text-white/70 text-sm">{pedido.refund_request.merchant_notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className={`rounded-[2rem] border ${currentStatus.border} ${currentStatus.bg} backdrop-blur-md p-8 mb-8 relative overflow-hidden transition-all duration-300`}>
           <div className={`absolute top-0 right-0 p-8 opacity-10 ${currentStatus.color}`}>
               <StatusIcon className="w-32 h-32" />
           </div>
           
           <div className="relative z-10">
              <div className="flex justify-between items-start">
                  <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2">Estado del Pedido</p>
                      <h1 className={`text-3xl md:text-4xl font-bold ${currentStatus.color} tracking-tight flex items-center gap-3`}>
                        <StatusIcon className="h-8 w-8" />
                        {currentStatus.label}
                      </h1>
                      <p className="text-white/80 text-sm mt-3 max-w-lg leading-relaxed font-medium">
                        {currentStatus.description}
                      </p>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                      {showRefundButton && (
                          <button onClick={() => setShowRefundModal(true)} className="bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-all flex items-center gap-2">
                              <RotateCcw className="h-4 w-4" /> Solicitar Devolución
                          </button>
                      )}
                      {showCancelButton && (
                          <button onClick={() => setShowCancelModal(true)} className="bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-all flex items-center gap-2">
                              <X className="h-4 w-4" /> Cancelar Pedido
                          </button>
                      )}
                  </div>
              </div>
           </div>
        </div>
        
        {showReturnInstructions && (
            <div className="rounded-[2rem] border border-orange-500/20 bg-orange-900/10 p-8 mb-8 animate-in slide-in-from-top-4">
                <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-orange-500/20 text-orange-400"><Box className="h-6 w-6" /></div>
                    <div className="flex-1">
                        <h3 className="text-lg font-bold text-orange-200 mb-2">Instrucciones para la Devolución</h3>
                        <div className="bg-black/40 border border-orange-500/10 rounded-xl p-4 mb-4">
                            <p className="text-sm text-white/90 whitespace-pre-wrap leading-relaxed">
                                {pedido.refund_request?.return_shipping_instructions || "Por favor contacta a la tienda para coordinar la entrega."}
                            </p>
                        </div>
                        {isNationalShipping ? (
                            <button onClick={() => setShowReturnShippingModal(true)} className="px-6 py-3 rounded-xl bg-orange-600 hover:bg-orange-500 text-white text-xs font-bold uppercase tracking-widest transition-all flex items-center gap-2 shadow-lg shadow-orange-900/20">
                                <Truck className="h-4 w-4" /> Ya envié el paquete (Registrar Guía)
                            </button>
                        ) : (
                            <div className="mt-4 bg-orange-500/20 border border-orange-500/30 p-3 rounded-xl flex items-center gap-3">
                                <div className="p-2 bg-orange-500/20 rounded-full"><Store className="h-5 w-5 text-orange-200" /></div>
                                <div>
                                    <p className="text-xs font-bold text-orange-200 uppercase tracking-widest mb-1">Entrega Pendiente</p>
                                    <p className="text-xs text-orange-100/80">Por favor acércate a la tienda para entregar el producto.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )}

        {pedido.status === 'return_shipped_by_customer' && pedido.refund_request?.return_shipping_receipt_url && (
            <div className="rounded-[2rem] border border-indigo-500/20 bg-indigo-900/10 p-8 mb-8 animate-in slide-in-from-top-4">
                <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-indigo-500/20 text-indigo-400"><Truck className="h-6 w-6" /></div>
                    <div className="flex-1">
                        <h3 className="text-lg font-bold text-indigo-200 mb-2">Devolución en Camino</h3>
                        <p className="text-sm text-white/60 mb-4">Tu paquete está en camino a la tienda.</p>
                        {pedido.refund_request.return_tracking_number && (
                            <div className="bg-black/40 border border-indigo-500/10 rounded-xl p-4 mb-4 flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] uppercase tracking-widest text-indigo-400/70 mb-1">Número de Tracking</p>
                                    <p className="text-lg font-mono text-white">{pedido.refund_request.return_tracking_number}</p>
                                </div>
                                <button onClick={() => { navigator.clipboard.writeText(pedido.refund_request.return_tracking_number); toast.success('Tracking copiado'); }} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-all">
                                    <Copy className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                        <div className="bg-black/40 border border-indigo-500/10 rounded-xl p-3">
                            <p className="text-[10px] uppercase tracking-widest text-indigo-400/70 mb-3">Comprobante de Envío</p>
                            <a href={pedido.refund_request.return_shipping_receipt_url} target="_blank" rel="noreferrer" className="block rounded-xl bg-black/50 border border-white/5 overflow-hidden h-64 relative group cursor-pointer">
                                <img src={pedido.refund_request.return_shipping_receipt_url} alt="Guía" className="w-full h-full object-contain opacity-90 group-hover:opacity-100 transition-opacity" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all flex items-end justify-center pb-6">
                                    <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black text-xs font-bold uppercase tracking-widest hover:bg-indigo-50 transition-colors">
                                        <ExternalLink className="h-4 w-4" /> Ver Comprobante Completo
                                    </span>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        )}

        <div className="grid md:grid-cols-3 gap-8">
           <div className="md:col-span-2 space-y-6">
              <div className="rounded-2xl border border-white/10 bg-[#0F0F0F]/80 backdrop-blur-md p-5 flex flex-col sm:flex-row items-center gap-4">
                 <div className="p-3 rounded-xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/20"><ExternalLink className="h-5 w-5" /></div>
                 <div className="flex-1 text-center sm:text-left">
                    <h3 className="text-sm font-bold text-white mb-1">Enlace de Seguimiento</h3>
                    <p className="text-xs text-white/40">Guarda este link para consultar tu pedido después.</p>
                 </div>
                 <button onClick={handleCopyLink} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold uppercase tracking-widest text-white transition-all flex items-center gap-2">
                    {copiedLink ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3" />} {copiedLink ? 'Copiado' : 'Copiar'}
                 </button>
              </div>

              <div className="rounded-[2rem] border border-white/10 bg-[#0F0F0F]/60 backdrop-blur-md p-8">
                 <h2 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-6 border-b border-white/5 pb-4">Items del Pedido</h2>
                 <div className="space-y-6">
                    {pedido.items?.map((item) => (
                       <div key={item.id} className="flex gap-4 items-start">
                          <div className="w-16 h-16 rounded-xl bg-black border border-white/10 overflow-hidden flex-shrink-0">
                             {item.product_image ? (
                                <img src={item.product_image} alt={item.product_name} className="w-full h-full object-cover" />
                             ) : (
                                <div className="w-full h-full flex items-center justify-center text-white/20"><ShoppingBag className="h-6 w-6" /></div>
                             )}
                          </div>
                          <div className="flex-1 min-w-0">
                             <p className="text-sm font-medium text-white">{item.product_name}</p>
                             <p className="text-xs text-white/40 mt-1">Cant: {item.quantity}</p>
                             {item.variant_attributes && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                   {Object.entries(item.variant_attributes).map(([k,v]) => (
                                      <span key={k} className="text-[9px] bg-white/5 border border-white/5 px-1.5 py-0.5 rounded text-white/50 uppercase">{k}: {v}</span>
                                   ))}
                                </div>
                             )}
                          </div>
                          <div className="text-right">
                             <p className="text-sm font-bold text-white">{formatCurrency(item.price * item.quantity)}</p>
                          </div>
                       </div>
                    ))}
                 </div>
                 
                 <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                    {pedido.shipping_cost > 0 && (
                       <>
                          <div className="flex justify-between items-center">
                             <span className="text-sm text-white/60">Subtotal</span>
                             <div className="text-right">
                                <p className="text-lg font-light text-white">{formatCurrency(pedido.delivery_is_own !== false ? pedido.total - pedido.shipping_cost : pedido.total)}</p>
                                {pricingRate && <p className="text-xs font-mono text-white/40">{formatDualPrice(pedido.delivery_is_own !== false ? pedido.total - pedido.shipping_cost : pedido.total, pricingRate).local}</p>}
                             </div>
                          </div>
                          <div className="flex justify-between items-center">
                             <div className="flex items-center gap-2">
                                <Truck className="h-4 w-4 text-indigo-400" />
                                <span className="text-sm text-white/60">Delivery {pedido.delivery_zone && `(${pedido.delivery_zone})`}{pedido.delivery_is_own === false && ' *'}</span>
                             </div>
                             <div className="text-right">
                                <p className="text-lg font-light text-white">{pedido.delivery_is_own === false ? `${formatCurrency(pedido.shipping_cost)} *` : `+${formatCurrency(pedido.shipping_cost)}`}</p>
                                {pricingRate && <p className="text-xs font-mono text-indigo-400">{pedido.delivery_is_own === false && '* '}{formatDualPrice(pedido.shipping_cost, pricingRate).local}</p>}
                             </div>
                          </div>
                          {pedido.delivery_is_own === false && pedido.shipping_cost > 0 && (
                             <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3 flex items-start gap-2 animate-in fade-in">
                                <AlertTriangle className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                                <div>
                                   <p className="text-xs font-bold text-yellow-400 mb-1">⚠️ Delivery Tercerizado</p>
                                   <p className="text-xs text-yellow-200/80 leading-relaxed">Debes pagar <span className="font-bold">{formatCurrency(pedido.shipping_cost)}</span>{pricingRate && <span> ({formatDualPrice(pedido.shipping_cost, pricingRate).local})</span>} directamente al repartidor. NO incluido en el total.</p>
                                </div>
                             </div>
                          )}
                          <div className="h-px bg-white/10" />
                       </>
                    )}
                    <div className="flex justify-between items-end">
                       <span className="text-sm text-white/60">Total {pedido.delivery_is_own === false && pedido.shipping_cost > 0 && '(a pagar a la tienda)'}</span>
                       <div className="text-right">
                          <p className="text-2xl font-light text-white tracking-tight">{formatCurrency(pedido.total)}</p>
                          {pricingRate && <p className="text-xs font-mono text-emerald-400 mt-1">{formatDualPrice(pedido.total, pricingRate).local}</p>}
                       </div>
                    </div>
                 </div>
              </div>

              <div className="space-y-6">
                 {(pedido.status === 'return_completed_refunded' || pedido.status === 'refund_completed' || pedido.status === 'refunded') && pedido.refund_request?.refund_receipt_url && (
                    <div className="rounded-[2rem] border border-green-500/20 bg-green-500/5 p-6 animate-in fade-in">
                       <div className="flex items-center gap-3 mb-4">
                          <Check className="h-5 w-5 text-green-400" />
                          <h3 className="text-sm font-bold text-white">Reembolso Completado</h3>
                       </div>
                       <p className="text-xs text-white/60 mb-4">La tienda ha enviado el comprobante de la devolución.</p>
                       <a href={pedido.refund_request.refund_receipt_url} target="_blank" rel="noreferrer" className="block rounded-xl bg-black/50 border border-white/5 overflow-hidden h-48 relative group cursor-pointer">
                          <img src={pedido.refund_request.refund_receipt_url} alt="Comprobante Reembolso" className="w-full h-full object-contain opacity-90 group-hover:opacity-100 transition-opacity" />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
                             <ExternalLink className="h-6 w-6 text-white" />
                          </div>
                       </a>
                    </div>
                 )}

                 {(pedido.status === 'return_completed_replaced') && pedido.refund_request?.replacement_receipt_url && (
                    <div className="rounded-[2rem] border border-cyan-500/20 bg-cyan-500/5 p-6 animate-in fade-in">
                       <div className="flex items-center gap-3 mb-4">
                          <RefreshCw className="h-5 w-5 text-cyan-400" />
                          <h3 className="text-sm font-bold text-white">Nuevo Producto Enviado</h3>
                       </div>
                       <p className="text-xs text-white/60 mb-4">Tu reemplazo va en camino.</p>
                       {pedido.refund_request.replacement_tracking && (
                           <div className="bg-black/40 border border-white/5 rounded-xl p-3 mb-3 flex justify-between items-center">
                               <span className="text-[10px] uppercase tracking-widest text-white/40">Tracking</span>
                               <span className="text-sm font-mono text-cyan-300">{pedido.refund_request.replacement_tracking}</span>
                           </div>
                       )}
                       <a href={pedido.refund_request.replacement_receipt_url} target="_blank" rel="noreferrer" className="block rounded-xl bg-black/50 border border-white/5 overflow-hidden h-48 relative group cursor-pointer">
                          <img src={pedido.refund_request.replacement_receipt_url} alt="Guía Reemplazo" className="w-full h-full object-contain opacity-90 group-hover:opacity-100 transition-opacity" />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
                             <ExternalLink className="h-6 w-6 text-white" />
                          </div>
                       </a>
                    </div>
                 )}

                 {pedido.status === 'pending_payment_confirmation' && !pedido.payment_receipt_url && (
                    <div className="rounded-[2rem] border border-amber-500/30 bg-amber-500/5 p-8 text-center">
                       <h3 className="text-lg font-bold text-white mb-2">Pago Pendiente</h3>
                       <p className="text-white/50 text-sm mb-6 max-w-md mx-auto">Para procesar tu envío, necesitamos confirmar tu pago. Sube la captura aquí.</p>
                       <label className="inline-flex flex-col items-center gap-2 cursor-pointer px-8 py-4 rounded-xl border border-dashed border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20 transition-all group">
                          <Upload className="h-6 w-6 text-amber-400 group-hover:scale-110 transition-transform" />
                          <span className="text-xs font-bold uppercase tracking-widest text-amber-200">Adjuntar Comprobante</span>
                          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleProofUpload} />
                       </label>
                       {file && <p className="text-xs text-emerald-400 mt-4 animate-pulse">Subiendo archivo...</p>}
                    </div>
                 )}

                 {pedido.receipt_correction_requested && pedido.payment_receipt_url && (
                    <div className="rounded-[2rem] border border-amber-500/30 bg-amber-500/5 p-8 animate-in slide-in-from-top-4">
                       <div className="flex items-center gap-3 mb-4">
                          <div className="p-3 rounded-full bg-amber-500/20 text-amber-400"><AlertTriangle className="h-6 w-6" /></div>
                          <div>
                             <h3 className="text-lg font-bold text-white">Comprobante Incorrecto</h3>
                             <p className="text-xs text-white/50">La tienda necesita que subas un comprobante válido</p>
                          </div>
                       </div>
                       <p className="text-white/70 text-sm mb-6 leading-relaxed">El comprobante que subiste no es válido o no coincide con los datos del pedido.</p>
                       <label className="inline-flex flex-col items-center gap-2 cursor-pointer px-8 py-4 rounded-xl border border-dashed border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20 transition-all group w-full">
                          <Upload className="h-6 w-6 text-amber-400 group-hover:scale-110 transition-transform" />
                          <span className="text-xs font-bold uppercase tracking-widest text-amber-200">Subir Nuevo Comprobante</span>
                          <input 
                             type="file" 
                             accept="image/*,application/pdf" 
                             className="hidden" 
                             onChange={async (e) => {
                                const selectedFile = e.target.files?.[0];
                                if (!selectedFile) return;
                                try {
                                   let finalFile = selectedFile;
                                   if (selectedFile.type.startsWith('image/')) {
                                      const compressedFile = await imageCompression(selectedFile, { maxSizeMB: 0.5, maxWidthOrHeight: 2000, useWebWorker: true, quality: 0.9, fileType: 'image/jpeg' });
                                      finalFile = new File([compressedFile], selectedFile.name, { type: compressedFile.type });
                                   }
                                   const { data } = await api.post(`/api/checkout/generate-upload-url/`, { fileName: finalFile.name, contentType: finalFile.type || 'application/octet-stream' });
                                   await fetch(data.uploadURL, { method: 'PUT', body: finalFile, headers: { 'Content-Type': finalFile.type || 'application/octet-stream' } });
                                   await api.post(`/api/orders/${ordenId}/update-receipt`, { email, receipt_path: data.filePath });
                                   toast.success('Comprobante actualizado correctamente');
                                   queryClient.invalidateQueries({ queryKey: ['orden', ordenId, email] });
                                } catch (error) { console.error(error); toast.error('Error al actualizar comprobante'); }
                             }}
                          />
                       </label>
                    </div>
                 )}

                 {pedido.payment_receipt_url && (
                    <div className="rounded-[2rem] border border-emerald-500/20 bg-[#0F0F0F]/60 p-6">
                       <div className="flex items-center gap-3 mb-4">
                          <Check className="h-5 w-5 text-emerald-400" />
                          <h3 className="text-sm font-bold text-white">Comprobante de Pago Recibido</h3>
                       </div>
                       <a href={pedido.payment_receipt_url} target="_blank" rel="noreferrer" className="block rounded-xl bg-black/50 border border-white/5 overflow-hidden h-48 relative group cursor-pointer">
                          <img src={pedido.payment_receipt_url} alt="Pago" className="w-full h-full object-contain opacity-80 group-hover:opacity-100 transition-opacity" />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
                             <ExternalLink className="h-6 w-6 text-white" />
                          </div>
                       </a>
                    </div>
                 )}

                 {(pedido.shipping_receipt_url || pedido.shipping_receipt) && (
                    <div className="rounded-[2rem] border border-indigo-500/20 bg-[#0F0F0F]/60 p-6">
                       <div className="flex items-center gap-3 mb-6">
                          <div className="p-2 rounded-lg bg-indigo-500/20 text-indigo-400"><Truck className="h-5 w-5" /></div>
                          <div>
                              <h3 className="text-sm font-bold text-white">Envío Registrado</h3>
                              <p className="text-[10px] text-white/40 uppercase tracking-widest">{pedido.shipping_agency || 'Agencia de Encomienda'}</p>
                          </div>
                       </div>
                       <div className="grid grid-cols-1 gap-4 mb-6">
                           {pedido.shipping_tracking_number && (
                               <div className="bg-black/40 border border-white/5 rounded-xl p-4 flex flex-col gap-4">
                                   <div className="flex items-center justify-between">
                                       <div className="flex items-center gap-2 text-indigo-300"><Hash className="h-4 w-4" /><span className="text-[10px] font-bold uppercase tracking-widest">Tracking / Guía</span></div>
                                       <div className="relative">
                                            <button onClick={() => setShowHelp(!showHelp)} className="text-white/30 hover:text-white transition-colors"><HelpCircle className="h-4 w-4" /></button>
                                            {showHelp && (
                                                <div className="absolute right-0 bottom-6 w-64 bg-black border border-white/20 rounded-xl p-3 shadow-2xl z-10 animate-in zoom-in-95">
                                                    <p className="text-[10px] text-white/80 leading-relaxed">Las agencias en Venezuela no permiten enlaces directos.</p>
                                                </div>
                                            )}
                                        </div>
                                   </div>
                                   <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
                                       <span className="text-lg font-mono text-white tracking-widest">{pedido.shipping_tracking_number}</span>
                                       {agencyConfig ? (
                                            <button onClick={() => handleSmartTracking(agencyConfig.url, pedido.shipping_tracking_number)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest text-white hover:brightness-110 transition-all shadow-lg ${agencyConfig.color}`}>
                                                <span>Rastrear en {agencyConfig.name}</span><ExternalLink className="h-3.5 w-3.5" />
                                            </button>
                                        ) : (
                                            <button onClick={handleCopyTracking} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 border border-white/10 text-white hover:bg-white/20 transition-all text-xs font-bold uppercase tracking-widest">
                                                {copiedTracking ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}{copiedTracking ? 'Copiado' : 'Copiar'}
                                            </button>
                                        )}
                                   </div>
                               </div>
                           )}
                           {pedido.shipping_estimated_arrival && (
                               <div className="bg-black/40 border border-white/5 rounded-xl p-4 flex items-center justify-between">
                                   <div className="flex items-center gap-2 text-emerald-300"><Clock className="h-4 w-4" /><span className="text-[10px] font-bold uppercase tracking-widest">Llegada Estimada</span></div>
                                   <div><span className="text-sm font-medium text-white">{formatDate(pedido.shipping_estimated_arrival)}</span></div>
                               </div>
                           )}
                       </div>
                       <a href={pedido.shipping_receipt_url || pedido.shipping_receipt} target="_blank" rel="noreferrer" className="block rounded-xl bg-black/50 border border-white/5 overflow-hidden h-64 relative group cursor-pointer">
                          <img src={pedido.shipping_receipt_url || pedido.shipping_receipt} alt="Envío" className="w-full h-full object-contain opacity-90 group-hover:opacity-100 transition-opacity" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all flex items-end justify-center pb-6">
                             <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black text-xs font-bold uppercase tracking-widest hover:bg-indigo-50 transition-colors">
                                <ExternalLink className="h-4 w-4" /> Ver Comprobante Completo
                             </span>
                          </div>
                       </a>
                    </div>
                 )}
              </div>
           </div>

           <div className="md:col-span-1 space-y-6">
              <div className="rounded-[2rem] border border-white/10 bg-[#0F0F0F]/80 backdrop-blur-md p-6 sticky top-32">
                 <h2 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-6 border-b border-white/5 pb-4">Datos de Entrega</h2>
                 <div className="space-y-5">
                    <div>
                       <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-1">Cliente</p>
                       <p className="text-white font-medium">{pedido.customer_name}</p>
                       <p className="text-white/50 text-sm">{pedido.customer_email}</p>
                    </div>
                    <div>
                       <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-1">Método</p>
                       <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-white">
                          {pedido.shipping_method === 'pickup' ? <Package className="h-3 w-3"/> : <Truck className="h-3 w-3"/>}
                          {pedido.shipping_method === 'pickup' ? 'Retiro en Tienda' : pedido.shipping_method === 'delivery' ? 'Delivery Local' : 'Envío Nacional'}
                       </div>
                    </div>
                    {(pedido.shipping_address || (pedido.shipping_agency && pedido.shipping_agency_address)) && (
                       <div>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-1">Dirección</p>
                          <p className="text-white/70 text-sm leading-relaxed bg-white/5 p-3 rounded-lg border border-white/5">
                             {pedido.shipping_method === 'national_shipping' ? `${pedido.shipping_agency} - ${pedido.shipping_agency_address}` : pedido.shipping_address}
                          </p>
                       </div>
                    )}
                    {pedido.notes && (
                       <div>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-1">Notas</p>
                          <p className="text-white/50 text-xs italic">"{pedido.notes}"</p>
                       </div>
                    )}
                    {(storeInfo?.phone || storeInfo?.settings?.whatsapp_number) && (
                       <div className="pt-4 border-t border-white/5">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-3">Contactar Tienda</p>
                          <div className="space-y-2">
                             {storeInfo.phone && (
                                <a href={`tel:${storeInfo.phone}`} className="flex items-center gap-3 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-300 hover:bg-blue-500/20 transition-all group">
                                   <Phone className="h-4 w-4" />
                                   <div className="flex-1">
                                      <p className="text-xs font-bold">Llamar</p>
                                      <p className="text-[10px] text-blue-200/70 font-mono">{storeInfo.phone}</p>
                                   </div>
                                   <ExternalLink className="h-3 w-3 opacity-50 group-hover:opacity-100" />
                                </a>
                             )}
                             {storeInfo?.settings?.whatsapp_enabled && storeInfo?.settings?.whatsapp_number && (
                                <a href={`https://wa.me/${storeInfo.settings.whatsapp_number.replace(/[^0-9]/g, '')}?text=Hola, tengo una consulta sobre mi pedido #${pedido.id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-xl bg-green-500/10 border border-green-500/20 text-green-300 hover:bg-green-500/20 transition-all group">
                                   <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center"><span className="text-white text-[10px] font-bold">W</span></div>
                                   <div className="flex-1">
                                      <p className="text-xs font-bold">WhatsApp</p>
                                      <p className="text-[10px] text-green-200/70">Consulta sobre pedido #{pedido.id}</p>
                                   </div>
                                   <ExternalLink className="h-3 w-3 opacity-50 group-hover:opacity-100" />
                                </a>
                             )}
                          </div>
                       </div>
                    )}
                 </div>
              </div>
           </div>

        </div>
      </main>
    </div>
  );
}

export default OrdenResumen;